import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  OfferingType,
  ProductCategory,
  TagType,
} from 'screens/AccessExpressV3/accessExpress.types';
import GlowingBorder from '../../BaseComponents/GlowingBorder';
import OfferBadge from './OfferBadge';
import CuratedOfferDetails from './CuratedOfferDetails';
import { TermType } from 'screens/SelectServices/utils';
import { ProductType } from 'screens/SelectServices/schemas';
import { observer } from 'mobx-react-lite';
import { useCartContext } from 'screens/AccessExpressV3/CartProvider';
import { SelectedPackages } from 'context/SingleServiceManagerContext';
import { StarfieldGifSimulation } from 'screens/AccessExpressV3/BaseComponents/StarfieldGifSimulation';
import {
  AlternativeComponents,
  getAlternativeOfferType,
} from 'screens/AccessExpressV3/utils';
import Skeleton from 'components/Skeleton/Skeleton';
import { AnimatePresence, motion } from 'framer-motion';

const OfferCard = observer(
  ({
    tag,
    isLoading,
    onAddToCart,
    offering,
    serviceSummaryData,
    category,
    isAllProducts,
    changeLocationTransition = false,
    sharedHeight,
    onSharedHeightUpdate,
    collapsed,
  }: {
    tag: TagType;
    isLoading: boolean;
    selectedOffer: string | null;
    onAddToCart: (
      tag?: string,
      mns?: SelectedPackages,
      graniteInstall?: boolean,
    ) => void;
    offering: OfferingType | undefined;
    serviceSummaryData?: (ProductType & { term: TermType }) | undefined;
    category: ProductCategory;
    isAllProducts?: boolean;
    changeLocationTransition?: boolean;
    sharedHeight?: number;
    onSharedHeightUpdate?: (h: number) => void;
    collapsed?: boolean;
  }) => {
    const { isItemAlreadyInCart, hasCircuitLimitReached } = useCartContext();
    const [graniteInstall, setGraniteInstall] = useState(true);
    const isSelected = isItemAlreadyInCart(offering?.id as string);
    const alternativeType = getAlternativeOfferType({
      tag,
      category,
      offering,
      isLoading,
      isAllProducts,
    });
    const isStarlink = category === 'Starlink' && !alternativeType;
    const isDisabled = hasCircuitLimitReached(category, offering?.id ?? '');

    const [bestOfferHeight, setBestOfferHeight] = useState(158);

    const [mns, setMNS] = useState<SelectedPackages>({
      essential: false,
      enhanced: false,
    });

    useEffect(() => {
      setMNS(
        serviceSummaryData?.type === 'broadband'
          ? {
              essential: true,
              enhanced: false,
            }
          : {
              essential: false,
              enhanced: true,
            },
      );
    }, [serviceSummaryData?.type]);

    const handleMNSChange = useCallback(() => {
      if (serviceSummaryData?.formal_broadband_pricing) {
        setMNS((prev) => ({ ...prev, essential: !prev.essential }));
      }
      if (serviceSummaryData?.formal_dia_pricing) {
        setMNS((prev) => ({ ...prev, enhanced: !prev.enhanced }));
      }
    }, [serviceSummaryData]);

    const handleGraniteInstallChange = useCallback(() => {
      setGraniteInstall((prev) => !prev);
    }, []);

    const cardRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      const element = cardRef.current;
      if (!element) return;

      const updateHeight = () => {
        const height = element.getBoundingClientRect().height;
        if (!height || height < 50) return;

        if (tag === 'Best offer') {
          setBestOfferHeight(height);
        } else {
          onSharedHeightUpdate?.(height);
        }
      };

      const resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(updateHeight);
      });

      resizeObserver.observe(element);
      window.addEventListener('resize', updateHeight);
      requestAnimationFrame(updateHeight);

      return () => {
        resizeObserver.disconnect();
        window.removeEventListener('resize', updateHeight);
      };
    }, [tag, onSharedHeightUpdate]);

    const getOfferContent = useCallback(() => {
      return (
        <div className="z-10" ref={cardRef}>
          {alternativeType ? (
            AlternativeComponents[alternativeType]
          ) : isLoading ? (
            <motion.div
              layout
              key="skeleton"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
              style={{
                height: tag === 'Best offer' ? bestOfferHeight : sharedHeight,
              }}
            >
              <Skeleton
                className="!bg-neutral-50 h-full w-full opacity-5"
                animate={false}
              />
            </motion.div>
          ) : offering ? (
            <motion.div
              layout
              key={offering?.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                delay: 2,
                opacity: { duration: 0.4, ease: 'easeInOut' },
                layout: { type: 'spring', stiffness: 100, damping: 15 },
              }}
            >
              <CuratedOfferDetails
                tag={tag}
                offering={offering}
                isSelected={isSelected}
                onAddToCart={(tag) => onAddToCart(tag, mns, graniteInstall)}
                serviceSummaryData={serviceSummaryData}
                mns={mns}
                onMNSChange={handleMNSChange}
                graniteInstall={graniteInstall}
                onGraniteInstallChange={handleGraniteInstallChange}
                category={category}
              />
            </motion.div>
          ) : null}
        </div>
      );
    }, [
      isLoading,
      tag,
      bestOfferHeight,
      sharedHeight,
      alternativeType,
      offering,
      isSelected,
      serviceSummaryData,
      mns,
      handleMNSChange,
      graniteInstall,
      handleGraniteInstallChange,
      category,
      onAddToCart,
    ]);

    return (
      <div
        className={clsx(
          'relative z-0 min-h-[200px] min-w-0 overflow-visible sm:w-full',
        )}
        onClick={(e) => {
          e.stopPropagation();
          if (!alternativeType && !isDisabled) {
            onAddToCart(tag, mns, graniteInstall);
          }
        }}
      >
        {tag === 'Best offer' && (
          <GlowingBorder
            category={category}
            className={clsx('transition-all duration-300 ease-in-out', {
              '!opacity-0': collapsed,
            })}
          />
        )}
        <div
          className={clsx(
            clsx(
              'flex h-full w-full flex-col gap-4 rounded-t-[4px] border border-[#47556980] p-6',
              tag !== 'Best offer' && '!rounded-t-none !border-t-0',
              tag === 'Best offer' && 'border-transparent shadow-xl',
              tag === 'Best offer' && isStarlink && 'bg-transparent',
              tag === 'Best offer' &&
                (alternativeType === 'Starlink'
                  ? 'bg-[#191925]'
                  : isSelected && !isLoading
                    ? 'bg-background-base-surface-2'
                    : 'bg-background-base-surface-1'),
              (tag !== 'Best offer' || (tag === 'Best offer' && isStarlink)) &&
                isSelected &&
                !isLoading &&
                '!bg-[#9796F30F]',
              tag === 'Premium choice' && 'border-r-0',
            ),
          )}
        >
          {alternativeType === 'Starlink' && <StarfieldGifSimulation />}
          <OfferBadge
            tag={tag}
            isLoading={isLoading || changeLocationTransition}
            alternativeType={alternativeType}
          />

          <AnimatePresence mode="wait" initial={false}>
            {getOfferContent()}
          </AnimatePresence>
        </div>
      </div>
    );
  },
);

export default OfferCard;
